import React, { useState } from 'react';
import { Save, Shield, Bell, Globe, Database, AlertTriangle, Check } from 'lucide-react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Settings states
  const [generalSettings, setGeneralSettings] = useState({
    platformName: 'ServiceFlow CZ',
    supportEmail: 'support@serviceflow.cz',
    adminEmail: 'admin@serviceflow.cz',
    defaultLanguage: 'cs',
    timeZone: 'Europe/Prague',
    maintenanceMode: false
  });

  const [paymentSettings, setPaymentSettings] = useState({
    stripePublicKey: '',
    stripeSecretKey: '',
    commissionRate: 10,
    payoutSchedule: 'weekly',
    minimumPayout: 500,
    currency: 'CZK',
    taxRate: 21
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    adminAlerts: true,
    providerNotifications: true,
    customerNotifications: true,
    bookingReminders: true,
    reviewNotifications: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorRequired: false,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requirePasswordSpecialChars: true,
    allowProviderSelfRegister: true,
    requireDocumentVerification: true
  });

  const [integrationSettings, setIntegrationSettings] = useState({
    googleMapsApiKey: '',
    googleAnalyticsId: '',
    facebookAppId: '',
    twilioAccountSid: '',
    twilioAuthToken: '',
    emailProvider: 'sendgrid',
    sendgridApiKey: ''
  });

  const handleSave = async (settingsType) => {
    setLoading(true);
    try {
      // Simulate API call
      setTimeout(() => {
        setSaved(true);
        setLoading(false);
        setTimeout(() => setSaved(false), 2000);
      }, 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Globe },
    { id: 'payments', name: 'Payments', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'integrations', name: 'Integrations', icon: Database }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name</label>
            <input
              type="text"
              value={generalSettings.platformName}
              onChange={(e) => setGeneralSettings({...generalSettings, platformName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
            <input
              type="email"
              value={generalSettings.supportEmail}
              onChange={(e) => setGeneralSettings({...generalSettings, supportEmail: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Email</label>
            <input
              type="email"
              value={generalSettings.adminEmail}
              onChange={(e) => setGeneralSettings({...generalSettings, adminEmail: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
            <select
              value={generalSettings.defaultLanguage}
              onChange={(e) => setGeneralSettings({...generalSettings, defaultLanguage: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="cs">Čeština (Czech)</option>
              <option value="en">English</option>
              <option value="sk">Slovenčina (Slovak)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>
            <select
              value={generalSettings.timeZone}
              onChange={(e) => setGeneralSettings({...generalSettings, timeZone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Europe/Prague">Europe/Prague</option>
              <option value="Europe/Bratislava">Europe/Bratislava</option>
              <option value="Europe/Vienna">Europe/Vienna</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="maintenanceMode"
              checked={generalSettings.maintenanceMode}
              onChange={(e) => setGeneralSettings({...generalSettings, maintenanceMode: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-900">
              Enable maintenance mode
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">When enabled, only administrators can access the platform</p>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Stripe Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Stripe Public Key</label>
            <input
              type="text"
              value={paymentSettings.stripePublicKey}
              onChange={(e) => setPaymentSettings({...paymentSettings, stripePublicKey: e.target.value})}
              placeholder="pk_live_..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Stripe Secret Key</label>
            <input
              type="password"
              value={paymentSettings.stripeSecretKey}
              onChange={(e) => setPaymentSettings({...paymentSettings, stripeSecretKey: e.target.value})}
              placeholder="sk_live_..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Commission & Payouts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Commission Rate (%)</label>
            <input
              type="number"
              value={paymentSettings.commissionRate}
              onChange={(e) => setPaymentSettings({...paymentSettings, commissionRate: parseInt(e.target.value)})}
              min="0"
              max="50"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Payout (CZK)</label>
            <input
              type="number"
              value={paymentSettings.minimumPayout}
              onChange={(e) => setPaymentSettings({...paymentSettings, minimumPayout: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
            <input
              type="number"
              value={paymentSettings.taxRate}
              onChange={(e) => setPaymentSettings({...paymentSettings, taxRate: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Payout Schedule</label>
          <select
            value={paymentSettings.payoutSchedule}
            onChange={(e) => setPaymentSettings({...paymentSettings, payoutSchedule: e.target.value})}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Channels</h3>
        <div className="space-y-4">
          {Object.entries(notificationSettings).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </p>
                <p className="text-xs text-gray-500">
                  {getNotificationDescription(key)}
                </p>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => setNotificationSettings({
                  ...notificationSettings,
                  [key]: e.target.checked
                })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Require Two-Factor Authentication</p>
              <p className="text-xs text-gray-500">Force all admin users to enable 2FA</p>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.twoFactorRequired}
              onChange={(e) => setSecuritySettings({...securitySettings, twoFactorRequired: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
              <input
                type="number"
                value={securitySettings.sessionTimeout}
                onChange={(e) => setSecuritySettings({...securitySettings, sessionTimeout: parseInt(e.target.value)})}
                min="5"
                max="1440"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
              <input
                type="number"
                value={securitySettings.maxLoginAttempts}
                onChange={(e) => setSecuritySettings({...securitySettings, maxLoginAttempts: parseInt(e.target.value)})}
                min="3"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Password Length</label>
              <input
                type="number"
                value={securitySettings.passwordMinLength}
                onChange={(e) => setSecuritySettings({...securitySettings, passwordMinLength: parseInt(e.target.value)})}
                min="6"
                max="20"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Provider Registration</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Allow Provider Self-Registration</p>
              <p className="text-xs text-gray-500">Let providers register directly without admin approval</p>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.allowProviderSelfRegister}
              onChange={(e) => setSecuritySettings({...securitySettings, allowProviderSelfRegister: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Require Document Verification</p>
              <p className="text-xs text-gray-500">Providers must upload business documents for verification</p>
            </div>
            <input
              type="checkbox"
              checked={securitySettings.requireDocumentVerification}
              onChange={(e) => setSecuritySettings({...securitySettings, requireDocumentVerification: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Maps & Location</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps API Key</label>
          <input
            type="password"
            value={integrationSettings.googleMapsApiKey}
            onChange={(e) => setIntegrationSettings({...integrationSettings, googleMapsApiKey: e.target.value})}
            placeholder="AIza..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">Required for location services and address autocomplete</p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Analytics & Tracking</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Google Analytics ID</label>
            <input
              type="text"
              value={integrationSettings.googleAnalyticsId}
              onChange={(e) => setIntegrationSettings({...integrationSettings, googleAnalyticsId: e.target.value})}
              placeholder="GA-XXXXXXXXX-X"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Facebook App ID</label>
            <input
              type="text"
              value={integrationSettings.facebookAppId}
              onChange={(e) => setIntegrationSettings({...integrationSettings, facebookAppId: e.target.value})}
              placeholder="Facebook App ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">SMS & Communication</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Twilio Account SID</label>
            <input
              type="password"
              value={integrationSettings.twilioAccountSid}
              onChange={(e) => setIntegrationSettings({...integrationSettings, twilioAccountSid: e.target.value})}
              placeholder="AC..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Twilio Auth Token</label>
            <input
              type="password"
              value={integrationSettings.twilioAuthToken}
              onChange={(e) => setIntegrationSettings({...integrationSettings, twilioAuthToken: e.target.value})}
              placeholder="Auth Token"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Email Service</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Provider</label>
            <select
              value={integrationSettings.emailProvider}
              onChange={(e) => setIntegrationSettings({...integrationSettings, emailProvider: e.target.value})}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="sendgrid">SendGrid</option>
              <option value="mailgun">Mailgun</option>
              <option value="ses">Amazon SES</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SendGrid API Key</label>
            <input
              type="password"
              value={integrationSettings.sendgridApiKey}
              onChange={(e) => setIntegrationSettings({...integrationSettings, sendgridApiKey: e.target.value})}
              placeholder="SG..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const getNotificationDescription = (key) => {
    const descriptions = {
      emailNotifications: 'Send notifications via email',
      smsNotifications: 'Send SMS notifications for urgent matters',
      pushNotifications: 'Browser push notifications',
      adminAlerts: 'Alert administrators of important events',
      providerNotifications: 'Notify providers of bookings and updates',
      customerNotifications: 'Send booking confirmations to customers',
      bookingReminders: 'Automatic booking reminder notifications',
      reviewNotifications: 'Notify when new reviews are submitted'
    };
    return descriptions[key] || '';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'payments':
        return renderPaymentSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'integrations':
        return renderIntegrationSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Settings</h1>
          <p className="text-gray-600 mt-1">Configure platform settings, integrations, and security</p>
        </div>
        {saved && (
          <div className="mt-4 sm:mt-0 flex items-center text-green-600">
            <Check className="h-4 w-4 mr-2" />
            Settings saved successfully!
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Tabs Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-3" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="col-span-12 lg:col-span-9">
          <div className="card">
            {renderTabContent()}
            
            {/* Save Button */}
            <div className="flex items-center justify-end pt-6 mt-6 border-t border-gray-200">
              <button
                onClick={() => handleSave(activeTab)}
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning for Sensitive Settings */}
      {(activeTab === 'payments' || activeTab === 'security' || activeTab === 'integrations') && (
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Security Notice</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You are modifying sensitive settings. Please ensure all values are correct before saving.
                Changes to payment and security settings may affect platform operations.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;